import { ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import {
	currentTargetRequired,
	onKeyUpEscape,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { removeTrapFocus, trapFocus } from '@/scripts/core/global'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global'
import {
	CartAddWithSections, CartPartialWithSections,
	CartUpdateWithSections, getCartUpsell, getSectionHTMLForResource,
} from '@/scripts/core/cart-functions'
import { flushPendingDebounces, waitForQueueDrain } from '@/scripts/cart/cart-queue'

// Open guard uses a data attribute on document.body because:
// 1. Instance flags reset when reloadAndOpen() replaces cart-drawer via outerHTML
// 2. Module-level flags don't work — Vite duplicates CartDrawer across chunks,
//    each with its own module scope (BOGOS triggers open from a different chunk)
const OPENING_ATTR = 'data-cart-drawer-opening'

export class CartDrawer extends UcoastEl {
	static htmlSelector = 'cart-drawer'
	static selectors = {
		overlay: '[data-uc-cart-drawer-overlay]',
		innerEmpty: '[data-uc-cart-drawer-inner-empty]',
		inner: '[data-uc-cart-drawer-inner]',
		container: '#CartDrawer',
		cartLink: '#CartIconBubble',
		closeButton: '[data-uc-cart-drawer-close-button]',
		noteSummary: '[data-uc-cart-note-summary]',
		noteDetails: '[data-uc-cart-note-details]',
	}
	sectionApiIds = ['cart-drawer', 'cart-icon-bubble']
	activeElement?: HTMLElement
	constructor() {
		super()

		this.addEventListener(
			'keyup',
			(evt) => evt.code === 'Escape' && this.close()
		)
		this.getOverlay().addEventListener('click', this.close.bind(this))
		this.getCloseButton().addEventListener('click', this.close.bind(this))
		this.setHeaderCartIconAccessibility()

		// Guard checkout: prevent proceeding with only free samples
		this.addEventListener('click', (event) => {
			const btn = (event.target as HTMLElement).closest<HTMLButtonElement>('#CartDrawer-Checkout')
			if (btn) void this.guardCheckout(event, btn)
		})

		// Reset checkout button state on every pageshow. guardCheckout
		// disables the button before navigating to /checkout; if the user
		// hits back, the button state can survive via bfcache OR via the
		// browser's form-state restore on history navigation. Resetting
		// unconditionally is a no-op on fresh loads and fixes both cases.
		window.addEventListener('pageshow', () => {
			document.body.removeAttribute(OPENING_ATTR)
			this.resetCheckoutButton()
		})
	}

	private async guardCheckout(event: Event, btn: HTMLButtonElement) {
		event.preventDefault()

		btn.disabled = true
		btn.classList.add('cart-drawer__checkout-button--loading')

		try {
			// Flush any pending debounced changes and wait for all
			// in-flight operations to complete before checking server state
			flushPendingDebounces()
			await waitForQueueDrain()

			// Collect sample variant IDs from the DOM
			const sampleEls = this.querySelectorAll<HTMLElement>('[data-uc-sample]')
			const sampleIds = new Set(
				Array.from(sampleEls).map(el => parseInt(el.getAttribute('data-uc-sample')!, 10))
			)

			const res = await fetch('/cart.js')
			const cart = await res.json()

			const hasRealProducts = cart.items.some(
				(item: { variant_id: number }) => !sampleIds.has(item.variant_id)
			)

			if (!hasRealProducts) {
				this.setAttribute(ATTRIBUTES.cartEmpty, '')
				this.resetCheckoutButton()
				return
			}

			window.location.href = '/checkout'
		} catch {
			// On error, allow checkout to proceed
			window.location.href = '/checkout'
		}
	}

	private async refreshCartBubble() {
		const html = await getSectionHTMLForResource(window.location.href, 'cart-icon-bubble')
		if (!html) return
		const doc = new DOMParser().parseFromString(html, 'text/html')
		const freshSection = doc.querySelector('.shopify-section')
		const currentBubble = document.querySelector('#CartIconBubble')
		if (freshSection && currentBubble) {
			currentBubble.innerHTML = freshSection.innerHTML
		}
	}

	getOverlay() {
		return qsRequired(CartDrawer.selectors.overlay, this)
	}
	getCloseButton() {
		// literally for some reason i have to save this to a const or it doesnt work lmao
		const closeButton = qsRequired(CartDrawer.selectors.closeButton, this)
		return closeButton
	}

	setCloseButtonListener() {
		this.getCloseButton().removeEventListener('click', this.close.bind(this))
		this.getCloseButton().addEventListener('click', this.close.bind(this))
	}

	setHeaderCartIconAccessibility() {
		const cartLink = qsRequired(CartDrawer.selectors.cartLink)
		cartLink.setAttribute('role', 'button')
		cartLink.setAttribute('aria-haspopup', 'dialog')
		this.getCloseButton().removeEventListener('click', this.close.bind(this))
		this.getCloseButton().addEventListener('click', this.close.bind(this))
		cartLink.addEventListener('click', (event) => {
			event.preventDefault()
			this.open(cartLink)
		})
		cartLink.addEventListener('keydown', (event) => {
			if (event.code.toUpperCase() === 'SPACE') {
				event.preventDefault()
				this.open(cartLink)
			}
		})
	}

	private resetCheckoutButton() {
		const checkout = qsOptional<HTMLButtonElement>('#CartDrawer-Checkout', this)
		if (checkout) {
			checkout.disabled = false
			checkout.classList.remove('cart-drawer__checkout-button--loading')
		}
	}

	open(triggeredBy?: HTMLElement) {
		if (this.classList.contains('active') || document.body.hasAttribute(OPENING_ATTR) || document.body.classList.contains('overflow-hidden')) {
			return
		}
		document.body.setAttribute(OPENING_ATTR, '')
		if (triggeredBy) this.setActiveElement(triggeredBy)
		const cartDrawerNote = qsOptional(
			CartDrawer.selectors.noteSummary,
			this
		)
		if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
			this.setSummaryAccessibility(cartDrawerNote)
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add('animate', 'active')
			void window.Ucoast.mediaManager.loadAllInContainer(this)
		}, 1)

		this.addEventListener(
			'transitionend',
			() => {
				document.body.removeAttribute(OPENING_ATTR)
				const containerToTrapFocusOn = this.hasAttribute(
					ATTRIBUTES.cartEmpty
				)
					? qsRequired(CartDrawer.selectors.innerEmpty, this)
					: qsRequired(CartDrawer.selectors.container)
				const focusElement =
					qsOptional(CartDrawer.selectors.inner, this) ||
					qsRequired(CartDrawer.selectors.closeButton, this)
				trapFocus(containerToTrapFocusOn, focusElement)
			},
			{ once: true }
		)


		document.body.classList.add('overflow-hidden')
		document.documentElement.classList.add('overflow-hidden')
	}

	close() {
		document.body.removeAttribute(OPENING_ATTR)
		this.classList.remove('active')
		removeTrapFocus(this.activeElement)
		document.body.classList.remove('overflow-hidden')
		document.documentElement.classList.remove('overflow-hidden')
		void this.refreshCartBubble()
		window.setTimeout(() => {
			this.classList.remove('animate')
		}, 301)
	}

	setSummaryAccessibility(cartDrawerNote: HTMLElement) {
		const parentElement = cartDrawerNote.parentElement
		if (!parentElement)
			throw new Error(
				'setSummaryAccessibility failed - No parent element found'
			)
		cartDrawerNote.setAttribute('role', 'button')
		cartDrawerNote.setAttribute('aria-expanded', 'false')
		const nextElementSibling = cartDrawerNote.nextElementSibling

		if (
			nextElementSibling instanceof HTMLElement &&
			nextElementSibling.hasAttribute('id')
		) {
			cartDrawerNote.setAttribute('aria-controls', nextElementSibling.id)
		}

		cartDrawerNote.addEventListener('click', (event: MouseEvent) => {
			const currentTarget = currentTargetRequired(event)
			const isExpanded = qsRequired(
				CartDrawer.selectors.noteDetails,
				this
			).hasAttribute('open')
			currentTarget.setAttribute('aria-expanded', `${isExpanded}`)
		})

		parentElement.addEventListener('keyup', onKeyUpEscape)
	}

	renderContentsWithoutOpening(cart: CartAddWithSections | CartUpdateWithSections | CartPartialWithSections) {
		const sectionHtml = cart.sections['cart-drawer']
		if (sectionHtml) {
			const doc = new DOMParser().parseFromString(sectionHtml, 'text/html')
			// Targeted updates only — preserve cart-samples and checkout button
			const targetSelectors = ['[data-uc-cart-drawer-main]', '[data-item-count]', '[data-uc-cart-drawer-totals]', 'shipping-bar-wrapper']
			for (const selector of targetSelectors) {
				const fresh = qsOptional(selector, doc)
				const current = qsOptional(selector, this)
				if (fresh && current) {
					current.innerHTML = fresh.innerHTML
				}
			}
			this.syncCartSamples(doc)
		}
		// Update cart icon bubble
		const bubbleHtml = cart.sections['cart-icon-bubble']
		if (bubbleHtml) {
			const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html')
			const freshBubble = qsOptional(SELECTORS.cartLink, doc)
			const currentBubble = qsOptional(SELECTORS.cartLink)
			if (freshBubble && currentBubble) {
				currentBubble.innerHTML = freshBubble.innerHTML
			}
		}
		void window.Ucoast.mediaManager.loadAllInContainer(this)
	}

	renderContents(cart: CartAddWithSections | CartUpdateWithSections | CartPartialWithSections) {
		void getCartUpsell()?.hide(true)
		this.setActiveElement(document.activeElement)

		// Targeted updates — preserve cart-samples to avoid constructor cascade
		const sectionHtml = cart.sections['cart-drawer']
		if (sectionHtml) {
			const doc = new DOMParser().parseFromString(sectionHtml, 'text/html')
			const targetSelectors = ['[data-uc-cart-drawer-main]', '[data-item-count]', '[data-uc-cart-drawer-totals]', 'shipping-bar-wrapper']
			for (const selector of targetSelectors) {
				const fresh = qsOptional(selector, doc)
				const current = qsOptional(selector, this)
				if (fresh && current) {
					current.innerHTML = fresh.innerHTML
				}
			}
			// Check empty state from server HTML
			const isEmpty = doc.querySelector('cart-drawer[data-uc-cart-empty]') !== null
			if (isEmpty) {
				this.setAttribute(ATTRIBUTES.cartEmpty, '')
			} else {
				this.removeAttribute(ATTRIBUTES.cartEmpty)
			}
			this.syncCartSamples(doc)
		} else if (cart.items.length) {
			this.removeAttribute(ATTRIBUTES.cartEmpty)
		} else {
			this.setAttribute(ATTRIBUTES.cartEmpty, '')
		}

		// Update cart icon bubble
		const bubbleHtml = cart.sections['cart-icon-bubble']
		if (bubbleHtml) {
			const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html')
			const freshBubble = qsOptional(SELECTORS.cartLink, doc)
			const currentBubble = qsOptional(SELECTORS.cartLink)
			if (freshBubble && currentBubble) {
				currentBubble.innerHTML = freshBubble.innerHTML
			}
		}

		void window.Ucoast.mediaManager.loadAllInContainer(this)
		this.open()
	}

	/** Targeted update from section HTML string — preserves cart-samples and event listeners. */
	private updateFromSectionHTML(html: string) {
		const doc = new DOMParser().parseFromString(html, 'text/html')
		const targetSelectors = ['[data-uc-cart-drawer-main]', '[data-item-count]', '[data-uc-cart-drawer-totals]', 'shipping-bar-wrapper']
		for (const selector of targetSelectors) {
			const fresh = qsOptional(selector, doc)
			const current = qsOptional(selector, this)
			if (fresh && current) {
				current.innerHTML = fresh.innerHTML
			}
		}
		// Update empty state
		const isEmpty = doc.querySelector('cart-drawer[data-uc-cart-empty]') !== null
		if (isEmpty) {
			this.setAttribute(ATTRIBUTES.cartEmpty, '')
		} else {
			this.removeAttribute(ATTRIBUTES.cartEmpty)
		}
		this.syncCartSamples(doc)
		void window.Ucoast.mediaManager.loadAllInContainer(this)
	}

	/** Ensure cart-samples presence matches server state (insert if missing, remove if gone). */
	private syncCartSamples(doc: Document) {
		const freshSamples = doc.querySelector('cart-samples')
		const currentSamples = this.querySelector('cart-samples')
		if (freshSamples && !currentSamples) {
			// Server now includes cart-samples but DOM doesn't have it — inject it
			const ctas = this.querySelector('.cart-drawer__ctas')
			if (ctas) {
				ctas.parentElement!.insertBefore(freshSamples, ctas)
			}
		} else if (!freshSamples && currentSamples) {
			// Server no longer includes cart-samples — remove it
			currentSamples.remove()
		}
	}

	private async updateCartBubble() {
		const html = await getSectionHTMLForResource(window.location.href, 'cart-icon-bubble')
		if (!html) return
		const doc = new DOMParser().parseFromString(html, 'text/html')
		const freshBubble = qsOptional(SELECTORS.cartLink, doc)
		const currentBubble = qsOptional(SELECTORS.cartLink)
		if (freshBubble && currentBubble) {
			currentBubble.innerHTML = freshBubble.innerHTML
		}
	}

	async reloadWithoutOpening() {
		const html = await getSectionHTMLForResource(window.location.href, 'cart-drawer')
		if (!html) return
		this.updateFromSectionHTML(html)
		void this.updateCartBubble()
	}

	async reloadAndOpen() {
		void getCartUpsell()?.hide(true)
		const html = await getSectionHTMLForResource(window.location.href, 'cart-drawer')
		if (!html) {
			this.open()
			return
		}
		this.updateFromSectionHTML(html)
		void this.updateCartBubble()
		this.open()
	}

	getSectionsToRender(): ShopifySectionRenderingSchema[] {
		return [
			{
				id: 'cart-drawer',
				selector: CartDrawer.selectors.container,
			},
			{
				id: 'cart-icon-bubble',
				selector: SELECTORS.cartLink,
			},
		]
	}

	setActiveElement(element: Element | null) {
		if (!element) throw new Error('Active element is required')
		this.activeElement = element as HTMLElement
	}
}
